import { useCallback, useEffect, useRef, useState } from 'react';

/* ---------- 型定義 --------------------------------------------------- */

type QueryKey = unknown[];

interface CacheEntry<T> {
  data?: T;
  error?: unknown;
  promise?: Promise<T>;
  subscribers: Set<() => void>;
}

type QueryOptions<TData> = {
  enabled?: boolean;
  queryKey: QueryKey;
  queryFn: () => Promise<TData>;
};

type QueryResults<TData> = {
  isLoading: boolean;
  data: TData | undefined;
  error: unknown;
  /** 手動で再フェッチ */
  refetch: () => void;
};

/* ---------- シンプルなキャッシュ ------------------------------------ */

const cache = new Map<string, CacheEntry<unknown>>();

function getEntry<T>(key: string): CacheEntry<T> {
  let entry = cache.get(key);
  if (!entry) {
    entry = { subscribers: new Set() };
    cache.set(key, entry);
  }
  return entry as CacheEntry<T>;
}

/* ---------- Hook ----------------------------------------------------- */

export function useQuery<TData>({
  enabled = true,
  queryKey,
  queryFn,
}: QueryOptions<TData>): QueryResults<TData> {
  const key = JSON.stringify(queryKey);
  const entry = getEntry<TData>(key);

  /* 1) queryFn を ref に固定して依存を切る */
  const queryFnRef = useRef(queryFn);
  useEffect(() => {
    queryFnRef.current = queryFn;
  }, [queryFn]);

  /* 2) 強制レンダー用 */
  const [, bump] = useState(0);
  const forceRender = useCallback(() => bump((v) => v + 1), []);

  /* 3) フェッチを実行するユーティリティ（idempotent） */
  const runFetch = useCallback(() => {
    if (!enabled || entry.promise) return;

    entry.promise = queryFnRef
      .current()
      .then((d) => (entry.data = d))
      .catch((e) => (entry.error = e))
      .finally(() => {
        entry.promise = undefined;
        entry.subscribers.forEach((fn) => fn());
      });
  }, [enabled, entry]);

  /* 4) subscribe / unsubscribe */
  useEffect(() => {
    entry.subscribers.add(forceRender);
    runFetch(); // 初回 & key 変更時にだけ走る

    return () => {
      entry.subscribers.delete(forceRender);
    };
    // key が変わる＝キャッシュ切替のみで再実行
  }, [key, runFetch, forceRender]);

  /* 5) 公開 API */
  const isLoading =
    enabled &&
    !!entry.promise &&
    entry.data === undefined &&
    entry.error === undefined;

  const refetch = useCallback(() => {
    entry.data = undefined;
    entry.error = undefined;
    runFetch();
  }, [entry, runFetch]);

  return { isLoading, data: entry.data, error: entry.error, refetch };
}
